import { Tag, TagLabel, useColorModeValue, Tooltip } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { keyframes } from '@emotion/react';
import { useState } from 'react';

// Define animation keyframes
const floatAnimation = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-5px); }
  100% { transform: translateY(0px); }
`;

const pulseAnimation = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

interface WordBubbleProps {
  word: string;
  translation?: string;
  onClick: (word: string) => void;
  index: number;
}

export function WordBubble({ word, translation, onClick, index }: WordBubbleProps) {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);

  // Apply different animations based on index for a staggered effect
  const animationDelay = `${(index % 5) * 0.2}s`;

  // Color mode values
  const bubbleBg = useColorModeValue('blue.50', 'blue.900');
  const bubbleHoverBg = useColorModeValue('blue.100', 'blue.800');
  const bubbleColor = useColorModeValue('blue.800', 'blue.100');

  return (
    <Tooltip
      label={translation ? `${word}: ${translation}` : word}
      placement="top"
      hasArrow
    >
      <Tag
        size="lg"
        borderRadius="full"
        variant="subtle"
        bg={isHovered ? bubbleHoverBg : bubbleBg}
        color={bubbleColor}
        px={3}
        py={2}
        cursor="pointer"
        animation={`${floatAnimation} 3s infinite ease-in-out, ${pulseAnimation} 2s infinite ease-in-out`}
        style={{ animationDelay }}
        _hover={{
          transform: 'translateY(-5px) scale(1.05)',
          boxShadow: 'lg',
          bg: bubbleHoverBg
        }}
        transition="all 0.3s ease"
        onClick={() => onClick(word)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        boxShadow="sm"
      >
        <TagLabel fontWeight="medium">{word}</TagLabel>
      </Tag>
    </Tooltip>
  );
}